import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRoleName, useAuth } from '../../context/AuthContext';
import { TokenStorage } from '../../services/api';

const BASE_URL = process.env.REACT_APP_API_URL || 'https://code.mannaplag.mannaanalytics.com/api';


export default function SetupBranding() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [orgName, setOrgName] = useState(user?.organisation || '');
  const [website, setWebsite] = useState('');
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { setError('Logo must be under 5MB.'); return; }
    setLogoFile(f);
    setLogoPreview(URL.createObjectURL(f));
    setError('');
  };

  const handleSkip = () => navigate('/dashboard', { replace: true });

  const handleSubmit = async () => {
    if (!orgName.trim()) { setError('Please enter your organisation name.'); return; }
    setUploading(true);
    setError('');

    try {
      const token = TokenStorage.getAccess();
      const form = new FormData();
      form.append('org_name', orgName.trim());
      if (website.trim()) form.append('website', website.trim());
      if (logoFile) form.append('logo', logoFile);

      const res = await fetch(`${BASE_URL}/accounts/branding/logo/`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Upload failed');
      }

      navigate('/dashboard', { replace: true });
    } catch (e: any) {
      setError(e.message || 'Something went wrong.');
    } finally {
      setUploading(false);
    }
  };

  const roleName = getRoleName(user?.role);
  const accountTypeLabel = roleName === 'publisher' ? 'Publisher' : roleName === 'institution' ? 'Institution' : 'Institution';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-lg bg-navy mb-4"
            style={{ background: '#003c6c' }}>
            <svg width="24" height="24" viewBox="0 0 16 16" fill="none">
              <path d="M8 1L14 4.5V11.5L8 15L2 11.5V4.5L8 1Z" stroke="white" strokeWidth="1.5" fill="none" />
              <path d="M8 5L11 7V11L8 13L5 11V7L8 5Z" fill="white" />
            </svg>
          </div>
          <h1 className="text-[24px] font-black text-gray-900 tracking-tight">Set up your branding</h1>
          <p className="text-[14px] text-gray-500 mt-1">
            Your logo will appear on all plagiarism reports generated for your {accountTypeLabel.toLowerCase()}.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">

          {/* Progress indicator */}
          <div className="flex items-center gap-0 border-b border-gray-100">
            {['Register', 'Verify', 'Choose Plan', 'Branding'].map((step, i) => (
              <div key={step} className="flex-1 py-3 text-center">
                <span className={`text-[11px] font-bold ${i === 3 ? 'text-indigo-600' : 'text-gray-400'}`}>
                  {i < 3 ? '✓ ' : ''}{step}
                </span>
              </div>
            ))}
          </div>

          <div className="p-6 flex flex-col gap-5">

            {/* Logo upload */}
            <div>
              <label className="text-[12px] font-bold text-gray-700 uppercase tracking-wide block mb-2">
                Organisation Logo
              </label>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-gray-200 rounded-xl p-6 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50 transition-all"
              >
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" className="max-h-20 max-w-[200px] object-contain mb-2" />
                ) : (
                  <>
                    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center mb-3">
                      <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="text-gray-400">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <p className="text-[13px] font-bold text-gray-700">Click to upload logo</p>
                    <p className="text-[11px] text-gray-400 mt-1">PNG, JPG, SVG or WebP · Max 5MB</p>
                  </>
                )}
                {logoPreview && (
                  <p className="text-[11px] text-indigo-500 font-medium mt-1">Click to change</p>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
                className="hidden" onChange={handleFilePick} />
            </div>

            {/* Org name */}
            <div>
              <label className="text-[12px] font-bold text-gray-700 uppercase tracking-wide block mb-2">
                Organisation Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={orgName}
                onChange={e => setOrgName(e.target.value)}
                placeholder={`e.g. ${accountTypeLabel === 'Publisher' ? 'Springer Nature' : 'University of Delhi'}`}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[13px] font-medium text-gray-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
              />
            </div>

            {/* Website */}
            <div>
              <label className="text-[12px] font-bold text-gray-700 uppercase tracking-wide block mb-2">
                Website <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <input
                type="url"
                value={website}
                onChange={e => setWebsite(e.target.value)}
                placeholder="https://yourorganisation.com"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-[13px] font-medium text-gray-900 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all"
              />
            </div>

            {error && (
              <p className="text-[12px] text-red-500 font-medium bg-red-50 px-4 py-3 rounded-xl">{error}</p>
            )}

            {/* Preview note */}
            <div className="bg-indigo-50 rounded-xl px-4 py-3">
              <p className="text-[12px] text-indigo-700 font-medium">
                💡 Your logo replaces the Manna Plag logo on all similarity reports — giving your institution a fully branded experience.
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <button onClick={handleSkip}
                className="flex-1 py-3 border border-gray-200 rounded-xl text-[13px] font-bold text-gray-500 hover:bg-gray-50 transition-colors">
                Skip for now
              </button>
              <button onClick={handleSubmit} disabled={uploading}
                className="flex-1 py-3 rounded-xl text-[13px] font-black text-white transition-colors disabled:opacity-60"
                style={{ background: '#003c6c' }}>
                {uploading ? 'Saving...' : 'Save & Continue →'}
              </button>
            </div>

          </div>
        </div>

        <p className="text-center text-[11px] text-gray-400 mt-4">
          You can update this anytime from Account Settings
        </p>
      </div>
    </div>
  );
}
